import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import OneColumnPage from '../app/layout/OneColumnPage';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { BannerEditorRef } from '../components/BannerEditor';
import BannerEditor from '../components/BannerEditor';
import type { Banner } from '../components/BannerModalContent';
import InPlaceEditControls from '../components/InPlaceEditControls';
import InPlaceEditable from '../components/InPlaceEditable';
import SquarishCardGrid from '../components/SquarishCardGrid';
import SummaryCollapsibleContent from '../components/SummaryCollapsibleContent';
import TopicSquarishCard from '../components/TopicSquarishCard';
import { DECLINE_ACCESS_TO, GRANT_ACCESS_TO } from '../constants/params';
import RichTextDescriptionEditor from '../editor/RichTextDescriptionEditor';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { GroupUserRole } from '../graphql/generated/graphql';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { groupToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import PageMeta from '../utils/PageMeta';
import handleApolloError from '../utils/error/handleApolloError';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';
import { parseSlug } from '../utils/router/useSlug';
import useBeforeRouterLeaveConfirmWithModal from '../utils/useBeforeRouterLeaveConfirmWithModal';
import useIsEditingInPlace from '../utils/useIsEditingInPlace';
import useResettableState from '../utils/useResettableState';
import * as styles from './GroupView.module.scss';
import GroupViewControls from './GroupViewControls';
import GroupViewMembers from './GroupViewMembers';
import groupPolicyCheck, { GroupPolicyAction } from './groupPolicyCheck';
import useDeleteGroup from './useDeleteGroup';

const GROUP_VIEW_QUERY = gql(/** @complexity 38 */ `
  query GroupView($id: ID!) {
    group(id: $id) {
      id
      name
      description
      banner {
        original
      }
      requestors {
        id
      }
      membershipTopics {
        id
        ...TopicSquarishCard_topic
      }
      ...GroupViewMembers_group
      ...groupPolicyCheck_group
    }
  }
`);

const UPDATE_GROUP_MUTATION = gql(/** @complexity 4 */ `
  mutation GroupViewUpdateGroup(
    $id: ID!
    $banner: Upload
    $unsplashId: String
    $name: String
    $description: Json
  ) {
    updateGroup(
      id: $id
      banner: $banner
      unsplashId: $unsplashId
      name: $name
      description: $description
    ) {
      id
      name
      description
      banner {
        original
      }
    }
  }
`);

const ADD_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewAddUser($groupId: ID!, $userId: ID!) {
    user: addUserToGroup(groupId: $groupId, userId: $userId) {
      ...GroupViewMembers_member
    }
  }
`);

const UPDATE_USER_ROLE_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewUpdateUserRole(
    $groupId: ID!
    $userId: ID!
    $role: GroupUserRole!
  ) {
    user: updateUserInGroup(groupId: $groupId, userId: $userId, role: $role) {
      id
      name
    }
  }
`);

const REMOVE_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupViewRemoveUser($groupId: ID!, $userId: ID!) {
    user: removeUserFromGroup(groupId: $groupId, userId: $userId) {
      ...GroupViewMembers_member
    }
  }
`);

const REQUEST_MUTATION = gql(/** @complexity 6 */ `
  mutation GroupViewRequestAccess($id: ID!) {
    requestGroupAccess(id: $id) {
      id
      requestors {
        id
      }
    }
  }
`);

const useMembershipEffect = (
  handleUpdateUserRole: (
    id: string,
    role: GroupUserRole,
  ) => Promise<{
    data?: DocumentType<typeof UPDATE_USER_ROLE_MUTATION> | null;
  }>,
  handleRemoveUser: (
    id: string,
  ) => Promise<{ data?: DocumentType<typeof REMOVE_USER_MUTATION> | null }>,
) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const grantAccessTo = searchParams.get(GRANT_ACCESS_TO);

    if (grantAccessTo) {
      setSearchParams({}, { replace: true });

      handleUpdateUserRole(grantAccessTo, GroupUserRole.Member)
        .then(({ data }) => {
          const user = data?.user;
          if (user) {
            dispatch(
              UIActions.pushToast(
                groupToasts.accessGranted({
                  userName: user.name,
                }),
              ),
            );
          }
        })
        .catch((error) => {
          handleApolloError(error, 'NOT_FOUND', () =>
            dispatch(UIActions.pushToast(groupToasts.requestRejectedError)),
          );
        });
    }

    const declineAccessTo = searchParams.get(DECLINE_ACCESS_TO);

    if (declineAccessTo) {
      setSearchParams({}, { replace: true });

      handleRemoveUser(declineAccessTo).then(() =>
        dispatch(UIActions.pushToast(groupToasts.requestDeclined)),
      );
    }
  }, [
    dispatch,
    searchParams,
    setSearchParams,
    handleUpdateUserRole,
    handleRemoveUser,
  ]);
};

type EditGroupPayload = Pick<
  DocumentType<typeof GROUP_VIEW_QUERY>['group'],
  'name' | 'description' | 'banner'
>;

type EditGroupState = Pick<EditGroupPayload, 'name' | 'description'> & {
  banner: Banner | null;
};

function getInitialState(group?: EditGroupPayload) {
  return {
    name: group?.name ?? '',
    description: group?.description ?? '',
    banner: group?.banner?.original ? { image: group.banner.original } : null,
  };
}

const GroupView = () => {
  const { groupSlug } = useParams();

  const [groupId] = parseSlug(groupSlug ?? '');
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const [isEditing, setIsEditing] = useIsEditingInPlace();

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  useEffect(() => {
    if (isMobile && isEditing) {
      setIsEditing(false);
    }
  }, [isMobile, isEditing, setIsEditing]);

  const { data, previousData, error } = useQuery(GROUP_VIEW_QUERY, {
    variables: { id: groupId },
  });

  useErrorRedirectEffect({
    error,
    noun: 'group',
  });

  const group = data?.group;

  const [
    editGroupState,
    { update: updateEditGroupState, reset: resetEditGroupState },
  ] = useResettableState<EditGroupState>(getInitialState(group));

  useEffect(() => {
    if (!isEditing || (data != null && previousData == null)) {
      resetEditGroupState(getInitialState(group));
    }
  }, [isEditing, group, resetEditGroupState, data, previousData]);

  const bannerEditor = useRef<BannerEditorRef>(null);
  const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(true);

  const [updateGroup, { loading: isSaving }] = useMutation(
    UPDATE_GROUP_MUTATION,
  );

  const saveGroup = async () => {
    const bannerFile = await bannerEditor.current?.getBannerFile();
    const nextBanner =
      editGroupState.banner?.image !== currentBannerUrl
        ? bannerFile
        : undefined;

    const result = await updateGroup({
      variables: {
        id: groupId,
        name: editGroupState.name,
        description: JSON.stringify(editGroupState.description),
        banner: nextBanner,
        unsplashId: editGroupState.banner?.unsplashId,
      },
    });
    const resultGroup = result.data?.updateGroup;

    if (resultGroup) {
      // we need to use flush sync here because otherwise the state update will be batched
      // with the redirect happening via setIsEditing(false), but we need to make sure that
      // the state is resetted before the navigation happens so that the confirm modal from
      // useBeforeRouterLeaveConfirmWithModal will not show up
      flushSync(() => {
        resetEditGroupState(getInitialState(resultGroup));
      });
    }
  };

  useBeforeRouterLeaveConfirmWithModal({
    skip: !isEditing || !editGroupState.touched,
    onConfirm: saveGroup,
    title: 'Save changes?',
    message: "Please save your changes if you'd like to keep them.",
    confirmButtonText: 'Save',
    cancelButtonText: 'Discard',
  });

  const [addUserMutate] = useMutation(ADD_USER_MUTATION, {
    update: legacyUpdateQueryList(
      { query: GROUP_VIEW_QUERY, variables: { id: groupId } },
      ['group.members', 'group.allUsers'],
      'user',
      true,
    ),
  });

  const handleAddUser = useCallback(
    (userId: string) => addUserMutate({ variables: { groupId, userId } }),
    [addUserMutate, groupId],
  );

  const [removeUserMutate] = useMutation(REMOVE_USER_MUTATION, {
    update: legacyUpdateQueryList(
      { query: GROUP_VIEW_QUERY, variables: { id: groupId } },
      ['group.members', 'group.allUsers', 'group.owners', 'group.requestors'],
      'user',
      false,
    ),
  });
  const handleRemoveUser = useCallback(
    (userId: string) => removeUserMutate({ variables: { groupId, userId } }),
    [removeUserMutate, groupId],
  );

  const [updateUserRoleMutate] = useMutation(UPDATE_USER_ROLE_MUTATION, {
    refetchQueries: [{ query: GROUP_VIEW_QUERY, variables: { id: groupId } }],
  });
  const handleUpdateUserRole = useCallback(
    (userId: string, role: GroupUserRole) =>
      updateUserRoleMutate({ variables: { groupId, userId, role } }),
    [updateUserRoleMutate, groupId],
  );

  const [handleRequestAccess, { loading: isRequesting }] = useMutation(
    REQUEST_MUTATION,
    {
      variables: { id: groupId },
    },
  );

  const canEdit = groupPolicyCheck(GroupPolicyAction.UpdateGroup, user, group);
  const { id: userId } = useAppSelector((state) => state.session.user);

  useMembershipEffect(handleUpdateUserRole, handleRemoveUser);

  const onLeave = groupPolicyCheck(GroupPolicyAction.LeaveGroup, user, group)
    ? () =>
        handleRemoveUser(userId).then(() => {
          dispatch(UIActions.pushToast(groupToasts.youHaveBeenRemoved));
        })
    : undefined;
  const onJoin = groupPolicyCheck(GroupPolicyAction.JoinGroup, user, group)
    ? async () => {
        await handleAddUser(userId);
        await handleUpdateUserRole(userId, GroupUserRole.Owner);
        dispatch(UIActions.pushToast(groupToasts.youWereAddedAsOwner));
      }
    : undefined;

  const handleDelete = useDeleteGroup(groupId);

  const hasRequested = _.some(group?.requestors, { id: user.id });

  const onRequest = groupPolicyCheck(
    GroupPolicyAction.RequestMembership,
    user,
    group,
  )
    ? () =>
        handleRequestAccess().then(() => {
          dispatch(UIActions.pushToast(groupToasts.membershipRequested));
        })
    : undefined;

  if (!group) {
    return null;
  }

  const { name, banner, description } = group;
  const currentBannerUrl = banner?.original;
  const isShowingBanner = currentBannerUrl != null || isEditing;

  const handleEditClick = () => setIsEditing(true);
  const handleSave = async () => {
    await saveGroup();
    setIsEditing(false);
  };

  const handleCancel = () => setIsEditing(false);

  const bannerEditorState = (() => {
    if (isSaving) return 'saving';
    return isEditing ? 'editing' : 'viewing';
  })();

  const membershipTopics =
    group?.membershipTopics?.map((topic) => ({
      ...topic,
      membershipChildTopics: [],
    })) ?? null;

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title={`${name} - Slab`} />
      {isShowingBanner && (
        <BannerEditor
          ref={bannerEditor}
          currentBannerUrl={currentBannerUrl}
          state={bannerEditorState}
          onBannerChange={(nextBanner) =>
            updateEditGroupState({ banner: nextBanner })
          }
          banner={editGroupState.banner}
        />
      )}
      <div
        className={classNames(styles.container, {
          [styles.hasBanner]: isShowingBanner,
        })}
      >
        <OneColumnPage hasBanner={isShowingBanner}>
          <div className={styles.titleRow}>
            <h1>
              {isEditing ? (
                <InPlaceEditable
                  defaultValue={group.name}
                  onChange={(nextName) =>
                    updateEditGroupState({ name: nextName })
                  }
                  placeholder="Enter a Group Name"
                  autoFocus
                />
              ) : (
                name
              )}
            </h1>
            <div className={styles.controlsContainer}>
              {isEditing ? (
                <InPlaceEditControls
                  saving={isSaving}
                  savingDisabled={editGroupState.name === ''}
                  onCancelClick={handleCancel}
                  onSaveClick={handleSave}
                />
              ) : (
                <GroupViewControls
                  onLeave={onLeave}
                  onJoin={onJoin}
                  onRequest={onRequest}
                  requested={hasRequested}
                  isRequesting={isRequesting}
                  {...(canEdit
                    ? {
                        onEdit: handleEditClick,
                        onDelete: handleDelete,
                      }
                    : {})}
                />
              )}
            </div>
          </div>
          <div
            className={classNames(styles.description, {
              [styles.isEditing]: isEditing,
            })}
          >
            {isEditing ? (
              <RichTextDescriptionEditor
                readOnly={false}
                noTitleLine
                namespace="topicUserDescription"
                contentPlaceholder="Write a description right here..."
                defaultValue={description}
                onTextChange={(nextDescription) =>
                  updateEditGroupState({ description: nextDescription })
                }
                testId="groupDescriptionEditor"
              />
            ) : (
              <SummaryCollapsibleContent
                collapsed={isDescriptionCollapsed}
                onCollapsedChange={setIsDescriptionCollapsed}
                maxLines={6}
                content={description}
                typography="postWithNoTitleLine"
              />
            )}
          </div>
          <GroupViewMembers
            group={group}
            handleAddUser={handleAddUser}
            handleRemoveUser={handleRemoveUser}
            handleUpdateUserRole={handleUpdateUserRole}
          />
          {!!membershipTopics.length && (
            <div className={styles.membershipTopics}>
              <h3>Member of Topics</h3>
              <SquarishCardGrid>
                {membershipTopics.map((topic) => (
                  <SquarishCardGrid.Item key={topic.id}>
                    <TopicSquarishCard topic={topic} link />
                  </SquarishCardGrid.Item>
                ))}
              </SquarishCardGrid>
            </div>
          )}
        </OneColumnPage>
      </div>
    </PageLayout.Content>
  );
};

export default GroupView;
